import {
  inject,
  Component,
  ViewChild,
  DestroyRef,
  AfterViewChecked,
  ViewEncapsulation,
  NgZone,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { SuiInputComponent } from '@sui/input';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { GridStackOptions } from 'gridstack';
import { TranslateService } from '@ngx-translate/core';
import { GridstackComponent } from 'gridstack/dist/angular';
import {
  faEye,
  faSpinner,
  faPenToSquare,
} from '@fortawesome/free-solid-svg-icons';

import { LoggerService } from '@services/logger.service';
import { UtilsService } from '../../services/utils.service';
import { SidebarService } from '../../services/sidebar.service';
import { WidgetViewProperties } from '../../services/feed.service';
import { PresentationService } from '../../services/presentation.service';
import { ObjectEditorService } from '../../services/object-editor.service';
import { ObjectActionsService } from '../../services/object-actions.service';
import { GridsterConfigService } from '../../services/gridster-config.service';
import { DashboardAutoRefreshManager } from '../../services/dashboard-auto-refresh-manager';
import { PanelComponentMappingService } from '../../services/panel-component-mapping.service';
import { DashboardPageCoordinatorService } from '../../services/dashboard-page-coordinator.service';

import {
  SidebarButtonConfig,
  SidebarButtonBehavior,
} from '../../components/sidebar/sidebar.component';
import { ShareMode } from '../../components/popups/share-dialog/share-dialog';

import {
  Widget,
  Dashboard,
  DashboardPage,
  ObjectPermission,
} from 'src/app/core/models/core.models';
import { UpdateDashboardPageParams } from '../../models/widget.models';
import {
  WidgetType,
  NomObjectClass,
  NomObjectLifecycleStatus,
} from '../../constants';
import { AppFeatures } from '../../app.features';

import { activateFilters } from '../../actions/feed.actions';
import {
  selectObjects,
  clearSelectedObjects,
} from '../../actions/selected.actons';
import {
  fetchWorkspaceObjects,
  objectChanged,
  updateDashboard,
} from '../../actions/core.actions';
import * as widgetActions from '../../actions/widget.actions';
import { createWidget } from '../../actions/widget.actions';
import {
  createDashboardPage,
  deleteDashboardPage,
  loadExportSchedules,
  selectDashboardPage,
  updateDashboardPage,
  reorderDashboardPages,
  loadDashboardViewPresetSuccess,
  deleteDashboardPageSuccess,
  updateDashboardUIState,
} from '../../actions/object.actions';

import {
  selectCurrentObject,
  selectDashboardPages,
  selectSelectedPageId,
  selectIsLoadingWidgets,
  selectObjectIsExporting,
  selectCurrentPageWidgets,
  selectCurrentObjectEditMode,
  selectSelectedPlaceholderId,
  selectWidgetsByFilterString,
  selectObjectWidgetViewProperties,
  selectExportSchedulesCount,
} from '../../reducers';
import { AppState } from 'src/app/reducers';

import {
  tap,
  map,
  take,
  pluck,
  filter,
  interval,
  switchMap,
  Observable,
  debounceTime,
  BehaviorSubject,
} from 'rxjs';
import { UserFacadeService } from '@services/user-facade.service';
import { produce } from 'immer';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DashboardAutoRefreshManager],
})
export class DashboardComponent implements AfterViewChecked, OnDestroy {
  private _gridstack: GridstackComponent;
  @ViewChild(GridstackComponent)
  set gridstack(value: GridstackComponent) {
    // ! we have this "setter" approach because using it in the ngAfterViewInit - this.gridstack is undefined (waiting widgets to come)
    this._gridstack = value;
    if (value) {
      this.setupGridstackListeners();
    }
  }
  get gridstack(): GridstackComponent {
    return this._gridstack;
  }

  widgets$: Observable<Array<Widget>>;
  private pendingScrollWidgetId: string | null = null;
  selectSelectedPlaceholderId$: Observable<string>;
  currentObjectEditMode$: Observable<boolean>;
  isDownloading$: Observable<boolean>;
  objectViewProperties$: Observable<WidgetViewProperties>;
  widgetsLoading$: Observable<boolean>;

  // * track which Widget is currently being resized
  resizingWidgetId$ = new BehaviorSubject<string | null>(null);
  // * track if any widget is currently being dragged
  isDragging$ = new BehaviorSubject<boolean>(false);

  // * Multi-page dashboard support - NgRx state-driven properties
  dashboardPages$: Observable<DashboardPage[]>;
  selectedPageId$: Observable<number>;
  currentPageWidgets$: Observable<Widget[]>;
  selectedPage$: Observable<DashboardPage | null>;

  // * Presentation mode state
  showPresentationMode = false;
  presentationSinglePageId: number | null = null;

  // * Edit state for Page Title and Description
  editingTitle = false;
  editingDescription = false;
  editTitleValue = '';
  editDescriptionValue = '';
  private titleFocused = false;
  private descriptionFocused = false;

  loadWidgetPassed = false;

  @ViewChild('titleInput') titleInput?: SuiInputComponent;
  @ViewChild('descriptionInput')
  descriptionInput?: SuiInputComponent;

  gridsterConfig: GridStackOptions;
  dashboard: Dashboard;
  dashboardId: string;
  sidebarButtons: SidebarButtonConfig[];
  exportSchedulesCount: number;

  faEye = faEye;
  faSpinner = faSpinner;
  faPenToSquare = faPenToSquare;
  WidgetType = WidgetType;

  private destroyRef = inject(DestroyRef);
  protected userFacade = inject(UserFacadeService);
  private dashboardAutoReloadManager = inject(DashboardAutoRefreshManager);

  autoReloadSecondsLeft$ = this.dashboardAutoReloadManager.secondsLeft$;
  cacheEnabled$ = this.dashboardAutoReloadManager.cacheEnabled$;

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private sidebarPanelService: PanelComponentMappingService,
    public objectActionService: ObjectActionsService,
    private translateService: TranslateService,
    private utilsService: UtilsService,
    private sidebarService: SidebarService,
    private logger: LoggerService,
    private gridsterConfigService: GridsterConfigService,
    private objectEditorService: ObjectEditorService,
    private presentationService: PresentationService,
    private pageCoordinator: DashboardPageCoordinatorService,
  ) {
    //
    this.store.dispatch(
      fetchWorkspaceObjects({
        workspaceId: this.route.snapshot.params['workspaceId'],
        lifecycleStatus: [NomObjectLifecycleStatus.ACTIVE],
      }),
    );

    this.gridsterConfig = this.gridsterConfigService.getDashboardConfig();
    this.objectViewProperties$ = this.store.select(
      selectObjectWidgetViewProperties,
    );

    // * Direct selector usage - no component-level enhancement needed
    this.widgets$ = this.store.select(selectWidgetsByFilterString).pipe(
      debounceTime(500),
      map((widgets) => {
        return Array.from(widgets.values()).map((widget) =>
          this.utilsService.deepCopy(widget),
        );
      }),
    );

    // * NgRx selectors for multi-page support
    this.dashboardPages$ = this.store.select(selectDashboardPages);
    this.selectedPageId$ = this.store.select(selectSelectedPageId);
    this.currentPageWidgets$ = this.store.select(selectCurrentPageWidgets);

    // * Computed observable for selected page details
    this.selectedPage$ = this.store.select(selectDashboardPages).pipe(
      switchMap((pages) =>
        this.store.select(selectSelectedPageId).pipe(
          map(
            (selectedId) =>
              pages.find((page) => page.id === selectedId) || null,
          ),
          map((page) => {
            return {
              ...page,
              description: page.description || 'No Description',
            };
          }),
        ),
      ),
    );

    this.selectSelectedPlaceholderId$ = this.store.select(
      selectSelectedPlaceholderId,
    );

    this.currentObjectEditMode$ = this.store.select(
      selectCurrentObjectEditMode,
    );

    this.isDownloading$ = this.store.select(selectObjectIsExporting);
    this.widgetsLoading$ = this.store.select(selectIsLoadingWidgets);

    // * Listen for presentation mode actions
    this.setupPresentationModeListener();
  }

  public ngOnInit(): void {
    this.route.data
      .pipe(
        // 1) pull dashboard straight out of the data object
        pluck('dashboard'),

        // 2) only continue if there actually is a dashboard
        filter((dashboard): dashboard is Dashboard => !!dashboard),

        // 3a) local side-effects
        tap((dashboard) => {
          this.dashboard = dashboard;
          this.dashboardId = dashboard.id;

          this.setupSidebar(dashboard);
        }),

        // 3b) store side-effects
        tap((dashboard) => {
          this.store.dispatch(objectChanged({ object: dashboard }));

          // * initial Page Selection
          this.pageCoordinator
            .initializeDashboard(dashboard, this.route)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        }),

        // 4) auto-unsubscribe when the component is destroyed
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // * listen for View Preset loading results
    this.setupViewPresetListener();

    // * listen for Page Deletion results
    this.setupPageDeletionListener();

    // * listen for widget creation success to scroll to new widget
    this.setupWidgetCreationListener();

    this.setupWidgetLoadActionSubscription();

    this.startDasbhoardSubscribtion();

    this.schedulesCountSubscription();

    // * Start auto-reload if enabled
    this.dashboardAutoReloadManager.startAutoRefresh();
  }

  private setupViewPresetListener(): void {
    this.actions$
      .pipe(
        ofType(loadDashboardViewPresetSuccess),
        tap((action) => {
          if (this.dashboard) {
            this.pageCoordinator.handleViewPresetLoaded(
              action.pageId,
              this.dashboard,
              this.route,
            );
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private setupPageDeletionListener(): void {
    this.actions$
      .pipe(
        ofType(deleteDashboardPageSuccess),
        tap((action) => {
          this.store
            .select(selectCurrentObject)
            .pipe(
              take(1),
              filter(
                (currentObject): currentObject is Dashboard =>
                  !!currentObject &&
                  currentObject.object_class_id === NomObjectClass.DASHBOARD,
              ),
              tap((dashboard) => {
                this.dashboard = dashboard;
                this.pageCoordinator.handlePageDeletion(
                  action.deletedPageId,
                  this.route,
                );
              }),
            )
            .subscribe();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private startDasbhoardSubscribtion() {
    this.store
      .select(selectCurrentObject)
      .pipe(
        filter(
          (currentObject): currentObject is Dashboard =>
            !!currentObject &&
            currentObject.object_class_id === NomObjectClass.DASHBOARD,
        ),
        tap((dashboard) => {
          // Side effect: assign
          this.dashboard = dashboard;
          this.setupSidebar(dashboard);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private schedulesCountSubscription() {
    this.store
      .select(selectExportSchedulesCount)
      .pipe(
        tap((count) => {
          this.exportSchedulesCount = count;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private setupGridstackListeners(): void {
    // ! this is the better way to track the Widget that is being resized
    // ! do not try with changeCB resize start/end events - it will not work as expected
    this.gridstack.grid?.on('resizestart', (event, el) => {
      const widgetId = el.gridstackNode?.id;
      if (widgetId) {
        this.resizingWidgetId$.next(widgetId.toString());
      }
    });

    this.gridstack.grid?.on('resizestop', (event, el) => {
      this.resizingWidgetId$.next(null);
    });

    this.gridstack.grid?.on('dragstart', (event, el) => {
      this.isDragging$.next(true);
    });

    this.gridstack.grid?.on('dragstop', (event, el) => {
      this.isDragging$.next(false);
    });
  }

  ngAfterViewInit(): void {
    this.route.queryParams.subscribe((params) => {
      const open = params['open'];
      if (open) {
        if (open == 'export-schedules') {
          this.store.dispatch(
            loadExportSchedules({
              objectId: this.dashboard.id,
              objectClassId: this.dashboard.object_class_id,
            }),
          );
          this.sidebarService.selectComponent(this.sidebarButtons[3], true);
        }
      }
    });
  }

  ngOnDestroy(): void {
    // console.log('--- DashboardComponent destroyed ---');
    this.dashboardAutoReloadManager.stopAutoRefresh();
    this.sidebarService.closeSidebar();
  }

  ngAfterViewChecked(): void {
    // * auto-focus inputs when entering edit mode (only once)
    if (this.editingTitle && this.titleInput && !this.titleFocused) {
      this.titleInput.focus();
      this.titleInput.select();
      this.titleFocused = true;
    }
    if (
      this.editingDescription &&
      this.descriptionInput &&
      !this.descriptionFocused
    ) {
      this.descriptionInput.focus();
      this.descriptionInput.select();
      this.descriptionFocused = true;
    }

    // ! check if we need to scroll to a newly created widget
    if (this.pendingScrollWidgetId) {
      const widgetElement = document.getElementById(
        `widget-${this.pendingScrollWidgetId}`,
      );
      if (widgetElement) {
        this.scrollToWidget(this.pendingScrollWidgetId);
        this.pendingScrollWidgetId = null;
      }
    }
  }

  loadComponent(sidebarButton: SidebarButtonConfig) {
    this.sidebarPanelService.getChildInstance().loadComponent(sidebarButton);
  }

  onGridChange(event: any) {
    if (event && event.nodes && event.nodes.length > 0) {
      const repositionedWidgets = event.nodes.map((node) => ({
        id: node.id,
        x: node.x,
        y: node.y,
        w: node.w,
        h: node.h,
      }));

      this.store.dispatch(
        widgetActions.widgetsRepositioned({ widgets: repositionedWidgets }),
      );
    }
  }

  trackByFn(index: number, widget: Widget): string {
    return widget.id;
  }

  selectAllWidgets(selected: boolean) {
    if (selected) {
      this.widgets$
        .pipe(
          take(1),
          map((widgets) => this.filterOutPlaceholders(widgets)),
          tap((filteredWidgets) =>
            this.store.dispatch(selectObjects({ objects: filteredWidgets })),
          ),
        )
        .subscribe();
    } else {
      this.store.dispatch(clearSelectedObjects());
    }
  }

  searchWidgets(query: string) {
    if (typeof query === 'string') {
      this.store.dispatch(widgetActions.searchWidgets({ query: query }));
    }
  }

  refreshWidgets() {
    this.store.dispatch(activateFilters());
  }

  private setupSidebar(dashboard: Dashboard) {
    const buttons: SidebarButtonConfig[] = [
      {
        label: this.translateService.instant('sidebar-tools.label.settings'),
        key: 'settings',
        header: this.translateService.instant('sidebar-tools.label.settings'),
        icon: ['fas', 'gear'],
        behaviorType: SidebarButtonBehavior.ACTION,
        actionHandler: () => {
          this.objectEditorService.loadDashboardAndOpenPopup(dashboard.id);
        },
        objectPermission: ['can_edit'],
        permission: AppFeatures.DASHBOARD_SETTINGS_ROOT, // Assuming this permission exists
      },
      {
        label: this.translateService.instant('sidebar-tools.label.widgets'),
        key: 'widgets',
        componentName: 'WidgetSidebarComponent',
        header: this.translateService.instant('sidebar-tools.label.widgets'),
        icon: ['fas', 'chart-bar'],
        objectPermission: ['can_edit'],
        inputs: {
          objectId: dashboard.id,
          objectClassId: NomObjectClass.DASHBOARD,
        },
      },
      // {
      //   label: this.translateService.instant('sidebar-tools.label.filters'),
      //   componentName: 'FiltersComponent',
      //   header: 'Filters',
      //   icon: ['fas', 'filter'],
      // },
      {
        label: this.translateService.instant('sidebar-tools.label.share'),
        componentName: 'ExportComponent',
        key: 'export',
        header: this.translateService.instant('sidebar-tools.label.share'),
        permission: AppFeatures.EXPORT_AND_SHARE_ROOT,
        icon: ['fas', 'share-nodes'],
        objectPermission: ['can_view'],
        inputs: {
          objectId: dashboard.id,
          objectClassId: NomObjectClass.DASHBOARD,
          shareMode: ShareMode.RESULTSET,
        },
      },
      {
        label: this.translateService.instant('sidebar-tools.label.schedules'),
        componentName: 'SchedulesComponent',
        header: this.translateService.instant('sidebar-tools.label.schedules'),
        permission: AppFeatures.SYSTEM_EXPORT_SCHEDULES,
        key: 'schedules',
        icon: ['fas', 'calendar'],
        objectPermission: ['can_view'],
        inputs: {
          objectId: dashboard.id,
          objectClassId: NomObjectClass.DASHBOARD,
        },
        counter: this.exportSchedulesCount,
      },
    ];

    this.sidebarButtons = buttons.filter(
      (b) =>
        this.userFacade.hasAllObjectPermissionsSync(
          dashboard,
          b.objectPermission ?? [],
        ) &&
        (!b.permission ||
          this.userFacade.hasPermissionSync(b.permission) === true),
    );
  }

  private filterOutPlaceholders(widgets: Widget[]): Widget[] {
    return widgets.filter((widget) => widget.widget_type_id !== 10);
  }

  selectPage(pageId: number): void {
    this.pageCoordinator.selectPage(pageId, this.dashboardId, this.route);
  }

  // ! Multi-page dashboard support - add new page method
  createPage(): void {
    this.store.dispatch(createDashboardPage({ dashboardId: this.dashboardId }));
  }

  deletePage(pageId: number): void {
    // * Safety check: prevent deletion of last page
    this.dashboardPages$
      .pipe(
        take(1),
        tap((pages) => {
          if (pages && pages.length > 1) {
            this.store.dispatch(
              deleteDashboardPage({ dashboardId: this.dashboardId, pageId }),
            );
          }
        }),
      )
      .subscribe();
  }

  reorderPages(event: {
    orderedIds: number[];
    orderedPages: DashboardPage[];
    rollbackPages: DashboardPage[];
  }): void {
    this.store.dispatch(
      reorderDashboardPages({
        dashboardId: this.dashboardId,
        orderedIds: event.orderedIds,
        orderedPages: event.orderedPages,
        rollbackPages: event.rollbackPages,
      }),
    );
  }

  onPageVisibilityToggle(event: { pageId: number; visible: boolean }): void {
    this.dashboardPages$.pipe(take(1)).subscribe((pages) => {
      const page = pages.find((p) => p.id === event.pageId);
      if (!page) return;

      const updateParams: UpdateDashboardPageParams = {
        dashboard_id: this.dashboardId,
        page_id: event.pageId,
        is_visible_in_presentation: event.visible,
      };

      this.store.dispatch(
        updateDashboardPage({
          page: updateParams,
        }),
      );
    });
  }

  // * Presentation mode methods
  enterPresentationMode(): void {
    this.showPresentationMode = true;
    this.dashboardAutoReloadManager.setPresentationMode();
  }

  exitPresentationMode(): void {
    this.dashboardAutoReloadManager.setNormalMode();

    this.selectedPageId$.pipe(take(1)).subscribe((selectedPageId) => {
      // * Clear all widget state to ensure fresh loading after presentation mode
      this.store.dispatch(widgetActions.clearAllWidgets());

      // * Re-trigger page selection to load fresh widgets
      if (selectedPageId) {
        this.store.dispatch(selectDashboardPage({ pageId: selectedPageId }));

        // * Force widget visibility after fresh widgets are loaded
        this.currentPageWidgets$
          .pipe(
            take(1),
            tap((widgets) => {
              widgets.forEach((widget) => {
                this.store.dispatch(
                  widgetActions.widgetVisibilityChanged({
                    widgetId: widget.id,
                    isVisible: true,
                  }),
                );
              });
            }),
          )
          .subscribe();
      }
    });

    this.showPresentationMode = false;
    this.presentationSinglePageId = null;
  }

  private setupPresentationModeListener(): void {
    this.presentationService.presentationMode$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((event) => {
          if (event.action === 'show') {
            this.presentationSinglePageId = event.singlePageId ?? null;
            this.enterPresentationMode();
          } else if (event.action === 'hide') {
            this.exitPresentationMode();
          }
        }),
      )
      .subscribe();
  }

  // * Page title and description editing methods
  startEditingTitle(currentTitle: string): void {
    this.editingTitle = true;
    this.editTitleValue = currentTitle;
    // Focus will be handled in template with ViewChild if needed
  }

  startEditingDescription(currentDescription: string): void {
    this.editingDescription = true;
    this.editDescriptionValue = currentDescription || '';
  }

  saveTitle(): void {
    if (this.editTitleValue.trim()) {
      this.selectedPage$
        .pipe(
          take(1),
          filter((page): page is DashboardPage => !!page),
          tap((page) => {
            const updatedPage: UpdateDashboardPageParams = {
              dashboard_id: this.dashboardId,
              page_id: page.id,
              name: this.editTitleValue.trim(),
            };

            this.store.dispatch(updateDashboardPage({ page: updatedPage }));
          }),
        )
        .subscribe();
    }

    this.cancelEditTitle();
  }

  saveDescription(): void {
    this.selectedPage$
      .pipe(
        take(1),
        filter((page): page is DashboardPage => !!page),
        tap((page) => {
          const updatedPage: UpdateDashboardPageParams = {
            dashboard_id: this.dashboardId,
            page_id: page.id,
            description: this.editDescriptionValue.trim(),
          };

          this.store.dispatch(updateDashboardPage({ page: updatedPage }));
        }),
      )
      .subscribe();
    this.cancelEditDescription();
  }

  cancelEditTitle(): void {
    this.editingTitle = false;
    this.editTitleValue = '';
    this.titleFocused = false;
  }

  cancelEditDescription(): void {
    this.editingDescription = false;
    this.editDescriptionValue = '';
    this.descriptionFocused = false;
  }

  onTitleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveTitle();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEditTitle();
    }
  }

  onDescriptionKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.saveDescription();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEditDescription();
    }
  }

  onDragOver(event: DragEvent): void {
    this.ngZone.runOutsideAngular(() => {
      event.preventDefault();
      event.dataTransfer!.dropEffect = 'copy';
    });
  }

  onDrop(event: DragEvent): void {
    this.ngZone.runOutsideAngular(() => {
      event.preventDefault();

      // ! parse JSON outside zone
      const dragData = this.getDragDataOutsideZone(event);

      if (dragData?.type === 'widget-visualization-type') {
        // ! re-enter zone only for state updates
        this.ngZone.run(() => {
          this.createWidgetFromDrop(dragData);
        });
      }
    });
  }

  private createWidgetFromDrop(dragData: any): void {
    this.store.dispatch(
      createWidget({
        chartId: dragData.chartId,
        chartTypeId: dragData.chartTypeId,
        objectId: this.dashboard.id,
        objectClassId: NomObjectClass.DASHBOARD,
        placeholderId: null,
        dataSourceObjectId: dragData.dataSourceObjectId,
        dataSourceObjectClassId: dragData.dataSourceObjectClassId,
      }),
    );
  }

  private setupWidgetCreationListener(): void {
    this.actions$
      .pipe(
        ofType(widgetActions.createWidgetSuccess),
        takeUntilDestroyed(this.destroyRef),
        tap((action) => {
          this.pendingScrollWidgetId = action.widget.id;
        }),
      )
      .subscribe();
  }

  private setupWidgetLoadActionSubscription(): void {
    this.actions$
      .pipe(
        ofType(widgetActions.loadWidgetsSuccess),
        tap(() => {
          this.loadWidgetPassed = true;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private scrollToWidget(widgetId: string): void {
    const widgetElement = document.getElementById(`widget-${widgetId}`);
    const scrollContainer = document.querySelector('.card-container.widgets');

    if (widgetElement && scrollContainer) {
      // TODO: this is working only with block: 'nearest'
      // TODO: other options like 'center' are pushing the parents up
      // TODO: there are other ways to scroll using getBoundingClientRect and scrollTo
      widgetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  private getDragDataOutsideZone(event: DragEvent): any {
    try {
      const data = event.dataTransfer?.getData('application/json');
      return data ? JSON.parse(data) : null;
    } catch (error) {
      return null;
    }
  }
}
